/**
 * Your own fills, arriving as the replay clock reaches them.
 *
 * **This is the one idea in `docs/23` taken more or less directly from the
 * competitor, and it is named as borrowed there.** It solves a problem AEGIS
 * already had: the replay advanced the bars and nothing told you where you were
 * relative to your own executions. The chart shows you *where* you acted; this
 * shows you *in what order*, and watching the rows light up one at a time is
 * information no annotation on the chart conveys.
 *
 * ## The one deliberate difference
 *
 * They dim a future execution and still print its price. That is a spoiler: a
 * replay exists to test a read *without* knowing what happens next, and showing
 * the exit price before the replay reaches the exit hands the answer over. Rows
 * ahead of the clock render their price as `·····` — visibly withheld rather
 * than absent, so the trader can see that something is coming without being
 * told what it is.
 *
 * This is the same argument `ReplayControls` makes about hiding AI annotations
 * during replay, applied to the user's own history instead of the model's.
 */
import type { JSX } from 'react';
import { fmtNum, fmtTime } from '../lib/api.js';
import type { ExecutionDto } from '@aegis/api-client';

/** Rendered instead of a price the replay has not reached. */
const WITHHELD = '·····';

export function ExecutionList({
  executions, clockMs, zone, live,
}: {
  executions: readonly ExecutionDto[];
  /**
   * Where the replay clock is, in epoch ms. Null means no replay is running,
   * and everything is shown — reviewing a finished trade is not a spoiler.
   */
  clockMs: number | null;
  zone?: string | undefined;
  /** False while a replay is running, to explain why prices are hidden. */
  live: boolean;
}): JSX.Element | null {
  if (executions.length === 0) return null;

  const reached = (x: ExecutionDto): boolean =>
    clockMs === null || Date.parse(x.at) <= clockMs;

  const count = executions.filter(reached).length;

  return (
    <section className="execs" aria-label="Your executions">
      <h3 className="panel-h">
        YOUR EXECUTIONS
        {clockMs !== null && (
          <span className="execs-progress mono"> {count} / {executions.length}</span>
        )}
      </h3>
      <table className="execs-table">
        <thead>
          <tr>
            <th>Time{zone ? <span className="th-zone"> ({zone})</span> : null}</th>
            <th>Side</th>
            <th className="num">Price</th>
            <th className="num">Qty</th>
          </tr>
        </thead>
        <tbody>
          {executions.map((x, i) => {
            const on = reached(x);
            return (
              <tr key={`${x.at}-${x.side}-${i}`} className={on ? 'exec-on' : 'exec-off'}>
                <td className="mono">
                  <span className={`exec-dot ${on ? 'on' : ''}`} aria-hidden="true" />
                  {/* The time is never withheld: knowing an execution is coming
                      is not the same as knowing what price it happened at, and
                      hiding it would make the list unreadable as a sequence. */}
                  {fmtTime(x.at, zone)}
                </td>
                <td>
                  <span className={`exec-side exec-${x.side}`}>
                    {x.side === 'entry' ? 'IN' : 'OUT'}
                  </span>
                </td>
                <td className="num mono">
                  {on ? fmtNum(x.price) : <span className="exec-withheld">{WITHHELD}</span>}
                </td>
                <td className="num mono">{Number(x.quantity)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!live && clockMs !== null && count < executions.length && (
        <p className="execs-note">
          Prices ahead of the replay clock are hidden. A replay that showed your
          exit before it reached it would be answering the question it exists to ask.
        </p>
      )}
    </section>
  );
}
